/**
 * ArtifactList – Renders linked artifacts adaptively by type.
 *
 * - photo/screenshot → thumbnail
 * - pdf/document → file icon + name
 * - link → clickable URL card
 * - video/other → generic icon
 *
 * Each item shows an unlink (X) button when `canEdit` is true.
 */

import { X, FileText, Link2, Film, Image, File } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEntityArtifacts, useUnlinkArtifact } from '@/hooks/useArtifacts';
import type { LinkEntityType } from '@/lib/artifacts/constants';
import { toast } from 'sonner';

interface ArtifactListProps {
  shopId: string;
  entityType: LinkEntityType;
  entityId: string;
  canEdit?: boolean;
  className?: string;
}

export function ArtifactList({
  shopId,
  entityType,
  entityId,
  canEdit = false,
  className,
}: ArtifactListProps) {
  const { data: artifacts, isLoading } = useEntityArtifacts(entityType, entityId, shopId);
  const unlinkMutation = useUnlinkArtifact(entityType, entityId);

  if (isLoading || !artifacts || artifacts.length === 0) return null;

  const handleUnlink = async (linkId: string) => {
    try {
      await unlinkMutation.mutateAsync(linkId);
      toast.success('Attachment removed');
    } catch {
      toast.error('Failed to remove attachment');
    }
  };

  return (
    <div className={`flex flex-wrap gap-2 ${className || ''}`}>
      {artifacts.map((a) => (
        <div
          key={a.linkId}
          className="group relative flex items-center gap-1.5 rounded-md border bg-muted/50 px-2 py-1 text-xs"
        >
          {/* Icon / thumbnail */}
          {a.type === 'photo' || a.type === 'screenshot' ? (
            a.signedUrl ? (
              <a href={a.signedUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={a.signedUrl}
                  alt={a.title || 'Evidence'}
                  className="h-8 w-8 rounded object-cover"
                />
              </a>
            ) : (
              <Image className="h-4 w-4 text-muted-foreground" />
            )
          ) : a.type === 'pdf' || a.type === 'scan_report' ? (
            <FileText className="h-4 w-4 text-muted-foreground" />
          ) : a.type === 'video' ? (
            <Film className="h-4 w-4 text-muted-foreground" />
          ) : a.type === 'link' ? (
            <Link2 className="h-4 w-4 text-muted-foreground" />
          ) : (
            <File className="h-4 w-4 text-muted-foreground" />
          )}

          {/* Label */}
          {a.type === 'link' && a.external_url ? (
            <a
              href={a.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="max-w-[120px] truncate text-primary underline"
            >
              {a.title || a.external_url}
            </a>
          ) : (
            <span className="max-w-[120px] truncate text-muted-foreground">
              {a.title || 'Attachment'}
            </span>
          )}

          {/* Unlink button */}
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleUnlink(a.linkId)}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
